import BrushedImage from "@/images/signomatic/brushed.png"
import WoodImage from "@/images/signomatic/wood.png"

export type ColorSet = [string, string]

const colormap: StringMap<string> = {
  "#ffffff": "white",
  "#000000": "black",
  "#004f86": "blue",
  "#009251": "green",
  "#c4271d": "red",
  "#fee900": "yellow",
  "#4f575b": "gray",
  "#daa520": "gold",
  "#bbbbbb": "silver",
  "#c27268": "rose_gold",
  "#f0f0f0": "transparent",
  "#e15616": "orange",
  "#141414": "black",
  "#e8cca1": "oak",
  "#8e7a64": "oak",
  "#eed895": "birch",
  "#be9261": "cherry",
  "#a67e6d": "cherry",
  "#f6e6b5": "maple",
  "#a88f71": "birch",
  "#8c4a20": "wood",
  "#ffe472": "brass",
  "#e3e0db": "nickel",
  "#d8c98d": "brass_blank",
  "#c8c6cd": "nickel_blank",
  "#252526": "nickel_oxide",
  "#497eaa": "blue",
  "#554585": "purple",
  "#76689a": "purple",
  "#523d2a": "brown",
  "#eb3f77": "pink",
  "#bc4077": "pink",
  "#52595d": "gray",
  "#eeeeee": "aluminium",
  "#e4e4e4": "frosty",
  "#e25303": "orange",
  "#dddddd": "aluminium",
  "#e3e0dc": "stainless",
  "#1f0f00": "dark_brown",
  "#008577": "dark_green",
}

const ColorUtil = {
  /**
   * Normalize hex color to full length lowercase. Expects string in #fff or #ffffff form.
   */
  normalize(color: string) {
    if (!color || color.length < 3 || color[0] !== "#") {
      return ""
    }

    let clr = color.substring(1, color.length)

    if (color.length < 6) {
      clr = clr
        .split("")
        .map(i => {
          return [i, i]
        })
        .reduce((a, b) => {
          return a.concat(b)
        }, [])
        .join("")
    }

    return "#" + clr.toLowerCase()
  },
  rgbToCmyk(r: number, g: number, b: number) {
    let c = 1 - r / 255
    let m = 1 - g / 255
    let y = 1 - b / 255
    let k = Math.min(c, Math.min(m, y))
    c = (c - k) / (1 - k)
    m = (m - k) / (1 - k)
    y = (y - k) / (1 - k)
    c = isNaN(c) ? 0 : c
    m = isNaN(m) ? 0 : m
    y = isNaN(y) ? 0 : y
    k = isNaN(k) ? 0 : k
    return {
      c,
      m,
      y,
      k,
    }
  },
  cmykToRgb(c: number, m: number, y: number, k: number) {
    c = c < 1 ? c : c / 100
    m = m < 1 ? m : m / 100
    y = y < 1 ? y : y / 100
    k = k < 1 ? k : k / 100
    c = c * (1 - k) + k
    m = m * (1 - k) + k
    y = y * (1 - k) + k
    let r = 1 - c
    let g = 1 - m
    let b = 1 - y
    r = Math.round(255 * r)
    g = Math.round(255 * g)
    b = Math.round(255 * b)
    return {
      r,
      g,
      b,
    }
  },
  isSet(color: ColorSet | string): color is ColorSet {
    return Array.isArray(color)
  },
  isColorSets(sets: ColorSet[] | string[]): sets is ColorSet[] {
    return Array.isArray(sets[0])
  },
  sameColor(a: ColorSet | string, b?: ColorSet | string) {
    if (!b) {
      return false
    }

    if (this.isSet(a)) {
      return a[0] === b[0] && a[1] === b[1]
    }

    return a === b
  },
  colorClass(color: ColorSet | string) {
    if (this.isSet(color)) {
      return [colormap[color[0]], colormap[color[1]]].join("_").toLowerCase()
    }

    const name = colormap[color as string]
    return name ? name.toLowerCase() : ""
  },
  isDisabled(disabled: ColorSet[], color: ColorSet | string) {
    if (!disabled) {
      return false
    }
    const background = color[0]
    const text = color[1]
    for (const el of disabled) {
      const disabledBackground = el[0]
      const disabledText = el[1]
      if (background === disabledBackground && text === disabledText) {
        return true
      }
    }
    return false
  },
  backgroundColor(color: ColorSet | string): string {
    if (this.isSet(color)) {
      return color[0]
    }

    return color as string
  },
  textColor(color: ColorSet | string): string {
    if (this.isSet(color)) {
      return color[1]
    }

    return color as string
  },
  backgroundImage(color: ColorSet | string): string {
    const hex = this.isSet(color) ? color[1] : (color as string)

    if (hex === "#e3e0db" || hex === "#ffe472") {
      return `url(${BrushedImage})`
    }

    if (hex === "#eeeeee") {
      return "linear-gradient(to bottom right, #e5e6e6 0%, #d7d7d7 100%)"
    }

    if (hex === "#daa520") {
      return "linear-gradient(to bottom right, #f9c561 0%, #d48347 100%)"
    }

    if (hex === "#bbbbbb") {
      return "linear-gradient(to bottom right, #f6f6f6 0%, #c7c7c7 100%)"
    }

    if (hex === "#c27268") {
      return "linear-gradient(to bottom right, #e09f9b 0%, #c27268 100%)"
    }

    return ["#8c4a20", "#a18d74", "#837361", "#9c7069", "#be9261", "#f6e6b5"].includes(hex) ? `url(${WoodImage})` : ""
  },
  filterCustomColor(color: string, signType: string, state: SignState) {
    let replacements: Array<[string, string]> = []

    if (!["plastic_sign", "name_sign"].includes(signType)) {
      replacements = [...replacements, ["#daa520", "#daa521"], ["#bbbbbb", "#bbbbbc"], ["#c27268", "#c27269"]]
    }

    if (signType !== "stainless_steel_sign" && !(signType === "label_plates" && state.material === "stainless")) {
      replacements = [...replacements, ["#e3e0dc", "#e3e0dd"]]
    }

    if (signType === "double_sided_sign" && state.material === "plastic") {
      replacements = [...replacements, ["#daa520", "#daa521"], ["#bbbbbb", "#bbbbbc"]]
    }

    if (signType !== "aluminium_sign" && !(signType === "double_sided_sign" && state.material === "regular")) {
      replacements = [...replacements, ["#eeeeee", "#eeeeef"]]
    }

    if (!["color_badge", "brass_sign"].includes(signType)) {
      replacements = [
        ...replacements,
        ["#ffe472", "#ffe473"],
        ["#e3e0db", "#e3e0dc"],
        ["#f0f0f0", "#f0f0f1"],
        ["#d8c98d", "#d8c98e"],
        ["#c8c6cd", "#c8c6ce"],
        ["#252526", "#252527"],
      ]
    }

    if (signType === "label_plates") {
      if (state.badgeName) {
        replacements = [
          ...replacements,
          ["#ffe472", "#ffe473"],
          ["#e3e0db", "#e3e0dc"],
          ["#f0f0f0", "#f0f0f1"],
          ["#d8c98d", "#d8c98e"],
          ["#c8c6cd", "#c8c6ce"],
          ["#252526", "#252527"],
        ]
      } else {
        replacements = [...replacements, ["#daa520", "#daa521"], ["#bbbbbb", "#bbbbbc"]]
      }
    }

    for (const special of replacements) {
      if (special[0] === color) {
        return special[1]
      }
    }

    return color
  },
  /**
   * Removes colors from colorsets that are not allowed for current sign.
   */
  filterColorSets(colors: ColorSet[], data?: SignData, signState?: SignState) {
    colors = [...colors]

    if (data?.suggested_thickness && signState?.thickness === 0.8) {
      return colors.filter(
        color => this.sameColor(color, ["#ffffff", "#000000"]) || this.sameColor(color, ["#141414", "#ffffff"])
      )
    }

    if (data?.name === "contour_cut") {
      return []
    }

    return colors
  },
  /**
   * Removes colors from suggested colors that are not allowed for content (objects, borders, etc).
   */
  filterSuggestedContentColors(colors: string[], data?: SignData) {
    colors = [...colors]

    if (data?.name === "acryl_alu_sign") {
      colors = colors.filter(color => !["#daa520", "#bbbbbb"].includes(color))
    }

    return colors.filter(color => !["#f0f0f0", "#eeeeee"].includes(color))
  },
}

export default ColorUtil
